import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { SupplierAuthService } from '../../features/painel/services/supplier-auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * Interceptor funcional para renovação automática de token
 * Seção 2.2 da especificação
 * 
 * Quando uma requisição retorna 401:
 * 1. Pausa outras requisições
 * 2. Tenta renovar o token via /v1/auth/refresh
 * 3. Se sucesso: atualiza token e repete requisições
 * 4. Se falha: redireciona para login
 */

let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(SupplierAuthService);
  const http = inject(HttpClient);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Se não é erro 401 ou já é uma requisição de refresh/login, não tenta renovar
      if (error.status !== 401 || isAuthRequest(req.url)) {
        return throwError(() => error);
      }

      // Se já está renovando, aguarda a renovação atual
      if (isRefreshing) {
        return refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(token => {
            const clonedReq = req.clone({
              setHeaders: { Authorization: `Bearer ${token}` }
            });
            return next(clonedReq);
          })
        );
      }

      // Inicia processo de renovação
      isRefreshing = true;
      refreshTokenSubject.next(null);

      const refreshToken = authService.getRefreshToken();

      if (!refreshToken) {
        isRefreshing = false;
        authService.logout();
        return throwError(() => error);
      }

      const apiBaseUrl = (window as any).API_BASE_URL || environment.API_BASE_URL;

      return http.post<any>(`${apiBaseUrl}/auth/refresh`, {
        refreshToken
      }).pipe(
        switchMap((response) => {
          isRefreshing = false;
          
          // Atualiza tokens armazenados
          authService.storeSession(response);
          refreshTokenSubject.next(response.accessToken);

          // Repete a requisição original com novo token
          const clonedReq = req.clone({
            setHeaders: { Authorization: `Bearer ${response.accessToken}` }
          });
          return next(clonedReq);
        }),
        catchError((refreshError) => {
          isRefreshing = false;
          authService.logout();
          return throwError(() => refreshError);
        })
      );
    })
  );
};

function isAuthRequest(url: string): boolean {
  return url.includes('/auth/login') || 
         url.includes('/auth/refresh') || 
         url.includes('/auth/logout');
}

